import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Package, Tags, Archive, AlertTriangle, DollarSign, BarChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProducts, deleteProduct, getCategories, deleteCategory } from '@/lib/api';
import type { Product, Category } from '@/types/firebase';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveCompanyId } from '@/hooks/useEffectiveCompanyId';
import { Card } from "@/components/ui/card";
import { formatCurrency } from '@/lib/utils';
import { AddCategoryDialog } from '@/components/dialogs/AddCategoryDialog';
import { AddEditProductDialog } from '@/components/dialogs/AddEditProductDialog';
import { showNotification } from '@/lib/generalNotifications';


export default function Products() {
  const { user } = useAuth();
  const { effectiveCompanyId } = useEffectiveCompanyId();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newlyCreatedCategoryId, setNewlyCreatedCategoryId] = useState<string | undefined>(undefined);


  // Get company ID - uses impersonated company when active
  const companyId = effectiveCompanyId || '';
  
  // Add debug logging for auth state
  useEffect(() => {
    console.log('[Products] User auth state:', {
      authenticated: !!user,
      companyId: companyId || 'none',
      userId: user?.uid || 'none'
    });
  }, [user, companyId]);

  // Fetch products and categories
  const { data: products = [], isLoading: isLoadingProducts, error: productsError } = useQuery({
    queryKey: ['products', companyId],
    queryFn: async ({ queryKey }) => {
      const [_, companyId] = queryKey;
      console.log('[Products] Fetching products with companyId:', companyId);
      if (!companyId) {
        console.error('[Products] No companyId available for products query');
        return [];
      }
      try {
        const result = await getProducts(companyId as string);
        console.log('[Products] Products fetched successfully:', result.length);
        return result;
      } catch (error) {
        console.error('[Products] Error fetching products:', error);
        throw error;
      }
    },
    enabled: !!companyId
  });

  // Add debug logging for query results
  useEffect(() => {
    console.log('[Products] Query state:', {
      isLoadingProducts,
      productsError: productsError?.message,
      productsCount: products?.length || 0
    });
  }, [products, isLoadingProducts, productsError]);

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories', companyId],
    queryFn: async ({ queryKey }) => {
      const [_, companyId] = queryKey;
      return getCategories(companyId as string);
    },
    enabled: !!companyId
  });

  // Mutations

  const deleteProductMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', companyId] });
      showNotification({
        title: "Success",
        message: "Product deleted successfully",
        type: "success"
      });
    },
    onError: () => {
      showNotification({
        title: "Error",
        message: "Failed to delete product",
        type: "error"
      });
    }
  });


  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!id) {
        throw new Error('Category ID is required');
      }
      return deleteCategory(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', companyId] });
      showNotification({
        title: "Success",
        message: "Category deleted successfully",
        type: "success"
      });
    },
    onError: (error: Error) => {
      showNotification({
        title: "Error",
        message: error.message,
        type: "error"
      });
    }
  });



  const filteredProducts = React.useMemo(() => {
    return products.filter(product => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        product.name.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower) ||
        (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
        (categories.find(c => c.id === product.category)?.name.toLowerCase().includes(searchLower) || false);

      if (selectedCategory) {
        return product.category === selectedCategory && matchesSearch;
      }

      return matchesSearch;
    });
  }, [products, searchTerm, selectedCategory, categories]);


  if (isLoadingProducts || isLoadingCategories) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">Products</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-500">Total Products</h3>
            <p className="text-2xl font-bold text-gray-900">{products.length}</p>
            <p className="text-sm text-blue-600">Active products</p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-full">Low Stock</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-500">Low Stock Items</h3>
            <p className="text-2xl font-bold text-gray-900">{products.filter(p => !p.isSubscription && !p.isService && (p.stockQuantity ?? 0) <= (p.lowStockAlert || 5)).length}</p>
            <p className="text-sm text-amber-600">Need attention</p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100/50 border-red-200 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <Archive className="h-6 w-6 text-red-600" />
            </div>
            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">Out of Stock</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-500">Out of Stock</h3>
            <p className="text-2xl font-bold text-gray-900">{products.filter(p => !p.isSubscription && !p.isService && (p.stockQuantity ?? 0) === 0).length}</p>
            <p className="text-sm text-red-600">Requires restock</p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Tags className="h-6 w-6 text-purple-600" />
            </div>
            <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">Value</span>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-500">Inventory Value</h3>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(products.reduce((sum, p) => {
              if (!p.isSubscription && !p.isService) {
                return sum + (p.price * (p.stockQuantity ?? 0));
              }
              return sum;
            }, 0))}</p>
            <p className="text-sm text-purple-600">{products.filter(p => p.isService).length} services</p>
          </div>
        </Card>
      </div>

      {/* Actions and Search */}
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-200">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsAddCategoryDialogOpen(true)}
                className="whitespace-nowrap"
              >
                Add Category
              </Button>
              <Button
                onClick={() => {
                  setSelectedProduct(null);
                  setIsAddDialogOpen(true);
                }}
                className="whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Product
              </Button>
            </div>
          </div>

          {/* Category Filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className={`rounded-full ${
                !selectedCategory
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => setSelectedCategory('')}
            >
              All
            </Button>
            {categories.map((category) => (
              <div key={category.id} className="relative group">
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    selectedCategory === category.id
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                  <span className="ml-2 text-xs">
                    ({products.filter(p => p.category === category.id).length})
                  </span>
                </Button>
                {/* Delete button - now outside the Button component */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
                      deleteCategoryMutation.mutate(category.id);
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 absolute -right-2 -top-2 p-1 rounded-full bg-red-100 hover:bg-red-200 transition-opacity"
                >
                  <Trash2 className="h-3 w-3 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Product</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Category</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">SKU</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Price</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Cost</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Stock</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50/50 transition-colors duration-150">
                  <td className="py-4 px-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{product.name}</span>
                      <span className="text-sm text-gray-500 line-clamp-1">{product.description}</span>
                      {product.isService && (
                        <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                          <Tags className="h-3 w-3" />
                          Service
                        </span>
                      )}
                      {product.isSubscription && (
                        <span className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                          <Tags className="h-3 w-3" />
                          {product.billingCycle} subscription
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-sm">
                      {categories.find(c => c.id === product.category)?.name || product.category}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-600">
                    {product.sku || '-'}
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="font-medium text-gray-900">{formatCurrency(product.price)}</div>
                    {product.isTaxable && (
                      <div className="text-xs text-gray-500">+Tax</div>
                    )}
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="font-medium text-gray-700">
                      {product.cost ? formatCurrency(product.cost) : '-'}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {product.isSubscription || product.isService ? (
                      <span className="text-gray-400 text-sm">N/A</span>
                    ) : (
                      <span className="text-sm">
                        {product.stockQuantity || 0} units
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-center">
                      {!product.isSubscription && !product.isService && (
                        (product.stockQuantity ?? 0) <= (product.lowStockAlert || 5) ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Low Stock
                          </span>
                        ) : (product.stockQuantity ?? 0) === 0 ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Out of Stock
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            In Stock
                          </span>
                        )
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedProduct(product);
                          setIsEditDialogOpen(true);
                        }}
                        className="text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this product?')) {
                            deleteProductMutation.mutate(product.id);
                          }
                        }}
                        className="text-gray-600 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Product Dialog */}
      <AddEditProductDialog
        isOpen={isAddDialogOpen || isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedProduct(null);
            setNewlyCreatedCategoryId(undefined);
          }
        }}
        companyId={companyId}
        product={selectedProduct}
        isEdit={isEditDialogOpen}
        onAddCategoryOpen={() => setIsAddCategoryDialogOpen(true)}
        preselectedCategoryId={newlyCreatedCategoryId}
      />

      {/* Add Category Dialog */}
      <AddCategoryDialog
        isOpen={isAddCategoryDialogOpen}
        onOpenChange={setIsAddCategoryDialogOpen}
        companyId={companyId}
        onCategoryCreated={(category) => {
          setNewlyCreatedCategoryId(category.id);
        }}
      />
    </div>
  );
}
